import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Target, Building, MapPin, CheckCircle, Star, Trophy, BarChart3,
  Users, Search, ChevronRight, X, ChevronDown, UserCheck, Eye, MessageSquare,
  ArrowLeft, Clock, Calendar, Phone
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import DMAnalyticsCharts from './DMAnalyticsCharts';
import MySheetsTab from './MySheetsTab';
import InformationForm from './InformationForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Reusable KPI Card Row (same layout as DMDashboard) ─── */
const KPIRow = ({ stats }) => {
  if (!stats?.supported) return null;
  const st = stats.stats || {};
  const totalLeads = st.totalLeads ?? 0;
  const pct = (val) => (totalLeads > 0 ? `${Math.round((val / totalLeads) * 100)}%` : '0%');
  const teamSize = (st.teamADMs ?? 0) + (st.teamCPs ?? 0);

  const cards = [
    { label: 'Total Leads',    value: totalLeads,              badge: '100%',                             color: 'from-blue-500 to-blue-600',    sub: 'text-blue-100',    bb: 'bg-blue-400/40',    icon: <Target className="w-4 h-4 opacity-80" />,       tid: 'cmd-stat-leads' },
    { label: 'Office Visits',  value: st.officeVisits ?? 0,    badge: pct(st.officeVisits ?? 0),          color: 'from-amber-500 to-amber-600',   sub: 'text-amber-100',   bb: 'bg-amber-400/40',   icon: <Building className="w-4 h-4 opacity-80" />,     tid: 'cmd-stat-office' },
    { label: 'Project Visits', value: st.projectVisits ?? 0,   badge: pct(st.projectVisits ?? 0),         color: 'from-orange-500 to-orange-600', sub: 'text-orange-100',  bb: 'bg-orange-400/40',  icon: <MapPin className="w-4 h-4 opacity-80" />,       tid: 'cmd-stat-project' },
    { label: 'Bookings',       value: st.bookings ?? 0,         badge: pct(st.bookings ?? 0),             color: 'from-emerald-500 to-emerald-600', sub: 'text-emerald-100', bb: 'bg-emerald-400/40', icon: <CheckCircle className="w-4 h-4 opacity-80" />, tid: 'cmd-stat-bookings' },
    { label: 'Confirm Sale',   value: st.confirmSales ?? 0,    badge: pct(st.confirmSales ?? 0),          color: 'from-indigo-500 to-indigo-600', sub: 'text-indigo-100',  bb: 'bg-indigo-400/40',  icon: <Star className="w-4 h-4 opacity-80" />,         tid: 'cmd-stat-confirm' },
    { label: 'Team (ADM+CP)',  value: teamSize,                 badge: `${st.teamADMs ?? 0}+${st.teamCPs ?? 0}`, color: 'from-teal-500 to-teal-600', sub: 'text-teal-100', bb: 'bg-teal-400/40',  icon: <Trophy className="w-4 h-4 opacity-80" />,       tid: 'cmd-stat-team' },
    { label: 'Overall Target', value: `${st.achievementPct ?? 0}%`, badge: `${st.monthlyAchieved ?? 0}/${st.monthlyTarget ?? 0}`, color: 'from-rose-500 to-rose-600', sub: 'text-rose-100', bb: 'bg-rose-400/40', icon: <BarChart3 className="w-4 h-4 opacity-80" />, tid: 'cmd-stat-target' },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="cmd-kpi-row">
      {cards.map((c) => (
        <Card key={c.label} className={`bg-gradient-to-br ${c.color} text-white border-0`}>
          <CardContent className="p-2.5 text-center">
            <div className="flex justify-center mb-0.5">{c.icon}</div>
            <p className="text-lg font-bold leading-tight" data-testid={c.tid}>{c.value}</p>
            <p className={`text-[10px] ${c.sub} mb-1.5 leading-tight`}>{c.label}</p>
            <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${c.bb}`}>{c.badge}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

/* ─── Main Panel ─── */
const CMDChannelPanel = ({ user, lockedCompany = null }) => {
  const [activeTab, setActiveTab] = useState('dm');
  const [search, setSearch] = useState('');
  // If lockedCompany is set (e.g. 'greenyuva'), always use it and hide the filter UI
  const [companyFilter, setCompanyFilter] = useState(lockedCompany || 'all');
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [showWorkbook, setShowWorkbook] = useState(false);
  const [convSummary, setConvSummary] = useState(null);
  const [convDetail, setConvDetail] = useState(null);      // full conv log for selected lead
  const [convDetailLoading, setConvDetailLoading] = useState(false);
  const [selectedConvLead, setSelectedConvLead] = useState(null); // {lead_name, lead_phone}
  const debounceRef = useRef(null);

  const token = localStorage.getItem('crm_token');
  const hdrs = { Authorization: `Bearer ${token}` };

  const fetchList = useCallback(async (tab, q, company) => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/hierarchy-accounts/list`, {
        headers: hdrs,
        params: { level: tab.toUpperCase(), search: q, limit: 50, skip: 0, company: company === 'all' ? '' : company },
      });
      setAccounts(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch {
      setAccounts([]);
    } finally {
      setLoadingList(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Debounced search — re-runs when tab, search, or companyFilter changes */
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchList(activeTab, search, companyFilter), search ? 350 : 0);
    return () => clearTimeout(debounceRef.current);
  }, [activeTab, search, companyFilter, fetchList]);

  /* Reset on tab switch */
  useEffect(() => {
    setSelected(null);
    setStats(null);
    setSearch('');
    setShowWorkbook(false);
    // Keep companyFilter when switching tabs — feels natural
  }, [activeTab]);

  const handleSelect = async (acc) => {
    setSelected(acc);
    setStats(null);
    setShowWorkbook(false);
    setConvSummary(null);
    setConvDetail(null);
    setSelectedConvLead(null);

    if (activeTab !== 'cp') {
      setLoadingStats(true);
      try {
        const [statsRes, convRes] = await Promise.all([
          axios.get(`${API}/my-sheets/stats/summary`, { headers: hdrs, params: { emp_id: acc.id } }),
          axios.get(`${API}/my-sheets/lead-conversations/cmd-summary/${acc.id}`, { headers: hdrs }),
        ]);
        setStats(statsRes.data);
        setConvSummary(convRes.data);
      } catch {
        setStats(null);
        setConvSummary(null);
      } finally {
        setLoadingStats(false);
      }
    }
  };

  const fetchConvDetail = async (empId, lead) => {
    setSelectedConvLead(lead);
    setConvDetail(null);
    setConvDetailLoading(true);
    try {
      const res = await axios.get(`${API}/my-sheets/lead-conversations/cmd-lead`, {
        headers: hdrs,
        params: { emp_id: empId, lead_phone: lead.lead_phone },
      });
      setConvDetail(res.data);
    } catch {
      setConvDetail({ conversations: [] });
    } finally {
      setConvDetailLoading(false);
    }
  };

  const TABS = [
    { key: 'dm',  label: 'DM',  badge: '30',   activeClass: 'bg-blue-600 text-white shadow-md',    idleClass: 'bg-stone-100 text-stone-600 hover:bg-blue-50 hover:text-blue-700' },
    { key: 'adm', label: 'ADM', badge: '300',  activeClass: 'bg-emerald-600 text-white shadow-md', idleClass: 'bg-stone-100 text-stone-600 hover:bg-emerald-50 hover:text-emerald-700' },
    { key: 'cp',  label: 'CP',  badge: '10K+', activeClass: 'bg-amber-500 text-white shadow-md',   idleClass: 'bg-stone-100 text-stone-600 hover:bg-amber-50 hover:text-amber-700' },
  ].filter(t => !lockedCompany || t.key === 'dm'); // Greenyuva channel_admin: DM tab only

  return (
    <Card data-testid="cmd-channel-panel" className="border-stone-200">
      <CardHeader className="pb-3 border-b border-stone-100">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserCheck className="w-5 h-5 text-blue-600" />
          DM Channel Drilldown
          <span className="text-xs font-normal text-stone-400 ml-1">— view &amp; operate any DM / ADM / CP account</span>
        </CardTitle>

        {/* Tab row */}
        <div className="flex gap-2 mt-3 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`cmd-channel-tab-${t.key}`}
              onClick={() => setActiveTab(t.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${activeTab === t.key ? t.activeClass : t.idleClass}`}
            >
              {t.label}
              <span className="ml-1.5 text-[10px] opacity-70">({t.badge})</span>
            </button>
          ))}
          {total > 0 && (
            <span className="ml-auto text-xs text-stone-400 self-center">
              {total.toLocaleString()} {activeTab.toUpperCase()}s in system
            </span>
          )}
        </div>

        {/* Company filter — visible on DM tab when NOT locked to a company */}
        {activeTab === 'dm' && !lockedCompany && (
          <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="company-filter-row">
            <span className="text-[11px] text-stone-400 font-medium">Company:</span>
            {[
              { key: 'all',       label: 'All',         cls: 'bg-stone-700 text-white', idle: 'bg-stone-100 text-stone-600 hover:bg-stone-200' },
              { key: 'dbk',       label: 'DBK Group',   cls: 'bg-blue-600 text-white',  idle: 'bg-blue-50 text-blue-700 hover:bg-blue-100' },
              { key: 'greenyuva', label: '🌿 Greenyuva', cls: 'bg-emerald-600 text-white', idle: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
            ].map((f) => (
              <button
                key={f.key}
                data-testid={`company-filter-${f.key}`}
                onClick={() => setCompanyFilter(f.key)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                  companyFilter === f.key ? f.cls : f.idle
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">

          {/* ── Left: Search + list ── */}
          <div className="lg:col-span-1">
            <div className="relative mb-2">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Name or ${activeTab.toUpperCase()} ID…`}
                className="pl-8 h-8 text-sm"
                data-testid="cmd-channel-search"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div
              className="space-y-0.5 max-h-[340px] overflow-y-auto rounded-lg border border-stone-100"
              data-testid="cmd-channel-list"
            >
              {loadingList ? (
                <div className="py-10 text-center text-stone-400 text-xs">Loading…</div>
              ) : accounts.length === 0 ? (
                <div className="py-10 text-center text-stone-400 text-xs">No results</div>
              ) : (
                accounts.map((acc) => {
                  const isActive = selected?.id === acc.id;
                  return (
                    <button
                      key={acc.id}
                      onClick={() => handleSelect(acc)}
                      data-testid={`cmd-channel-item-${acc.id}`}
                      className={`w-full text-left px-3 py-2 transition-all flex items-center justify-between group border-b border-stone-50 last:border-0 ${
                        isActive
                          ? 'bg-blue-50 text-blue-900'
                          : 'hover:bg-stone-50'
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-semibold truncate">{acc.name || acc.id}</p>
                          {acc.company === 'greenyuva' && (
                            <span data-testid={`greenyuva-badge-${acc.id}`} className="flex-shrink-0 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold tracking-wide">GY</span>
                          )}
                        </div>
                        <p className="text-[10px] text-stone-400">{acc.id} · {acc.mobile || '—'}</p>
                      </div>
                      <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ml-1 transition-opacity ${isActive ? 'opacity-100 text-blue-500' : 'opacity-0 group-hover:opacity-40'}`} />
                    </button>
                  );
                })
              )}
            </div>

            {accounts.length >= 50 && (
              <p className="text-[10px] text-stone-400 mt-1 text-center">Showing first 50 — refine search to see more</p>
            )}
          </div>

          {/* ── Right: Selected member data ── */}
          <div className="lg:col-span-3">
            {!selected ? (
              <div className="flex flex-col items-center justify-center h-64 rounded-xl border-2 border-dashed border-stone-200 text-stone-400">
                <Users className="w-10 h-10 mb-2 opacity-30" />
                <p className="text-sm">Select a {activeTab.toUpperCase()} from the list to view their data</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="cmd-channel-member-view">
                {/* Person header strip */}
                <div
                  className="flex items-center justify-between px-4 py-3 rounded-lg bg-gradient-to-r from-stone-800 to-stone-700 text-white"
                  data-testid="cmd-channel-member-header"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-sm">{selected.name || selected.id}</p>
                      {selected.company === 'greenyuva' && (
                        <span data-testid="selected-greenyuva-badge" className="px-2 py-0.5 rounded-full bg-emerald-400/80 text-white text-[10px] font-bold tracking-wide">🌿 Greenyuva</span>
                      )}
                    </div>
                    <p className="text-xs text-stone-300">{selected.id} · {activeTab.toUpperCase()} · {selected.mobile || 'No mobile'}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowWorkbook((v) => !v)}
                    data-testid="cmd-channel-workbook-btn"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 text-xs h-7"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    {showWorkbook ? 'Hide' : 'Open'} Workbook
                  </Button>
                </div>

                {/* KPI + Charts — only for DM / ADM */}
                {activeTab !== 'cp' && (
                  <>
                    {loadingStats ? (
                      <div className="flex items-center justify-center h-28 text-stone-400 text-sm">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mr-2" />
                        Loading analytics…
                      </div>
                    ) : stats?.supported ? (
                      <div className="space-y-4">
                        <KPIRow stats={stats} />
                        <DMAnalyticsCharts analytics={stats} loading={false} />
                      </div>
                    ) : (
                      <div className="py-6 text-center text-stone-400 text-sm rounded-lg border border-dashed border-stone-200">
                        {stats?.message || 'No analytics data — this account has no workbook data yet.'}
                      </div>
                    )}

                    {/* Conversation Summary + Lead Drilldown for CMD */}
                    {convSummary && (
                      <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4" data-testid="cmd-conv-summary">

                        {/* Header counts */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-indigo-600" />
                            <h4 className="font-bold text-indigo-700 text-sm">Lead Conversations</h4>
                          </div>
                          <div className="flex gap-2 text-xs">
                            <span className="px-2 py-1 rounded-full bg-indigo-100 text-indigo-700 font-bold" data-testid="cmd-conv-total">
                              {convSummary.total_conversations} entries
                            </span>
                            <span className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-500 border border-indigo-100">
                              {convSummary.total_leads_with_conv} leads
                            </span>
                          </div>
                        </div>

                        {/* If a lead is selected for drilldown */}
                        {selectedConvLead ? (
                          <div data-testid="cmd-conv-drilldown">
                            <button
                              onClick={() => { setSelectedConvLead(null); setConvDetail(null); }}
                              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 mb-3 font-semibold"
                              data-testid="cmd-conv-back-btn"
                            >
                              <ArrowLeft className="w-3.5 h-3.5" /> Back to lead list
                            </button>
                            <div className="bg-white rounded-lg border border-indigo-100 p-3 mb-2">
                              <p className="text-xs font-bold text-stone-700">{selectedConvLead.lead_name || 'Lead'}</p>
                              <p className="text-[10px] text-stone-400">{selectedConvLead.lead_phone} · {selectedConvLead.count} conversation entries</p>
                            </div>
                            {convDetailLoading ? (
                              <div className="flex items-center justify-center py-8 text-stone-400 text-xs gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-400" /> Loading…
                              </div>
                            ) : (convDetail?.conversations || []).length === 0 ? (
                              <p className="text-xs text-center text-indigo-400 py-4">No conversation entries found for this lead.</p>
                            ) : (
                              <div className="space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="cmd-conv-detail-list">
                                {[...(convDetail.conversations)].reverse().map((c, idx) => (
                                  <div key={idx} className="flex items-start gap-2.5 p-2.5 bg-white rounded-lg border border-indigo-100 text-xs" data-testid={`cmd-conv-item-${idx}`}>
                                    <span className="w-2 h-2 rounded-full bg-indigo-400 mt-1.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-0.5">
                                        <span className="font-semibold text-stone-700 capitalize">{(c.conv_type || '').replace(/_/g, ' ')}</span>
                                        <span className="text-[10px] text-stone-400 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{c.date}</span>
                                        {c.next_followup && <span className="text-[10px] text-amber-600 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />Follow-up: {c.next_followup}</span>}
                                      </div>
                                      <p className="text-stone-600 leading-relaxed">{c.remark}</p>
                                      {c.added_by_name && <p className="text-[10px] text-stone-400 mt-1">Logged by {c.added_by_name}</p>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Lead list + 5 recent entries */
                          <div className="space-y-3">
                            {/* Leads with conversations — clickable */}
                            {(convSummary.leads || []).length > 0 && (
                              <div>
                                <p className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wide mb-1.5">Leads — click to view full history</p>
                                <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="cmd-conv-leads-list">
                                  {convSummary.leads.map((lead, i) => (
                                    <button
                                      key={i}
                                      onClick={() => fetchConvDetail(selected.id, lead)}
                                      data-testid={`cmd-conv-lead-btn-${i}`}
                                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-white border border-indigo-100 hover:bg-indigo-50 hover:border-indigo-300 text-xs transition-all"
                                    >
                                      <div className="text-left min-w-0">
                                        <p className="font-semibold text-stone-800 truncate">{lead.lead_name || lead.lead_phone}</p>
                                        <p className="text-stone-400 text-[10px]">{lead.lead_phone}</p>
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className="px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-bold text-[10px]">{lead.count} entries</span>
                                        <ChevronRight className="w-3 h-3 text-indigo-400" />
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* 5 most recent entries across all leads */}
                            {convSummary.recent?.length > 0 && (
                              <div>
                                <p className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wide mb-1.5">5 Most Recent</p>
                                <div className="space-y-1.5">
                                  {convSummary.recent.slice(0, 5).map((c, idx) => (
                                    <div key={idx} className="flex items-start gap-2 p-2 bg-white rounded-lg border border-indigo-100 text-xs">
                                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 mt-1.5 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <span className="font-semibold text-stone-700">{c.date} · </span>
                                        <span className="text-stone-500 capitalize">{(c.conv_type || '').replace(/_/g, ' ')} — </span>
                                        <span className="text-stone-600 line-clamp-1">{c.remark}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {!convSummary.recent?.length && !(convSummary.leads || []).length && (
                              <p className="text-xs text-indigo-400 text-center py-2">No conversation entries logged yet for this {activeTab.toUpperCase()}.</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* CP: show basic info card when workbook is hidden */}
                {activeTab === 'cp' && !showWorkbook && (
                  <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-700">
                    CP account selected. Click <strong>Open Workbook</strong> to view their full sheet and Information Form.
                  </div>
                )}

                {/* Workbook — full MySheetsTab with empId for admin access */}
                {showWorkbook && (
                  <div
                    className="border border-stone-200 rounded-xl overflow-hidden"
                    data-testid="cmd-channel-workbook"
                  >
                    <MySheetsTab
                      key={selected.id}
                      user={user}
                      empId={selected.id}
                      ownerName={selected.name}
                      overviewContent={<InformationForm empId={selected.id} />}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CMDChannelPanel;
